import { useCallback, useState } from "react";
import { useToast } from "@astrum/ui";
import { bytesToMb, uploadLimitFor } from "@astrum/shared";
import { api } from "../../lib/api.js";
import type { Message } from "../../lib/api.js";
import { newClientMessageId } from "../../lib/chat-format.js";
import { downscaleImage } from "../../lib/downscale-image.js";
import { uploadFileWithProgress } from "../../lib/transport.js";
import { uploadFailure, uploadThrownMessage } from "../../lib/upload-error.js";
import type { UploadingFile } from "./types.js";

export interface FileUploads {
  /** Optimistic per-file upload state, one "uploading…" bubble each. */
  uploads: UploadingFile[];
  /** Drives the composer's paperclip spinner while anything is in flight. */
  attaching: boolean;
  attach: (file: File) => void;
  /** Cancels an upload in flight, or clears a failed one. */
  dismiss: (upload: UploadingFile) => void;
}

/**
 * Sharing files into a channel: upload, then post the message announcing it.
 *
 * Two steps rather than one because the upload endpoint scans before it
 * stores — a rejected file must leave no message behind claiming it was
 * shared. The file itself is listed in the channel's details panel.
 *
 * `reloadLatest` is the thread's own refetch, run once the message exists.
 */
export function useFileUploads(
  channelId: string | undefined,
  reloadLatest: (channelId: string) => Promise<void>,
): FileUploads {
  const { toast } = useToast();
  const [attaching, setAttaching] = useState(false);
  // Keyed by a client id so several concurrent uploads (a multi-file drop)
  // each get their own bubble and progress.
  const [uploads, setUploads] = useState<UploadingFile[]>([]);

  const attach = useCallback(
    (file: File) => {
      if (!channelId) return;

      // A client id so this file's optimistic bubble and progress are tracked
      // independently of any other file uploading at the same time.
      const clientId = newClientMessageId();
      const patchUpload = (patch: Partial<UploadingFile>) =>
        setUploads((prev) => prev.map((u) => (u.clientId === clientId ? { ...u, ...patch } : u)));
      const removeUpload = () => setUploads((prev) => prev.filter((u) => u.clientId !== clientId));

      // Reject an over-limit file before sending it: a large upload that the
      // server would refuse otherwise streams all its bytes only to fail — and
      // an over-limit body can be dropped mid-stream, which the browser reports
      // as an opaque network error rather than the server's clear message. This
      // shows the real reason instantly, with nothing sent.
      const limit = uploadLimitFor(file.type);
      if (file.size > limit) {
        setUploads((prev) => [
          ...prev,
          {
            clientId,
            fileName: file.name,
            sizeBytes: file.size,
            progress: 0,
            failed: true,
            error: `Too large — files must be under ${bytesToMb(limit)}MB.`,
            cancel: () => setUploads((p) => p.filter((u) => u.clientId !== clientId)),
          },
        ]);
        toast(`${file.name}: files must be under ${bytesToMb(limit)}MB.`, { tone: "danger" });
        return;
      }

      setAttaching(true);

      /*
       * Upload first, then post the message, then point the file at it.
       *
       * The upload has to go first: it scans before it stores, and a rejected
       * file must not leave a message behind claiming something was shared.
       *
       * `files.messageId` is what lets the thread render the image instead of
       * the words "Shared screenshot.png", so it is set immediately after the
       * message exists.
       */
      void downscaleImage(file).then((prepared) => {
        const form = new FormData();
        form.append("channelId", channelId);
        form.append("file", prepared);

        // A progress-reporting XHR upload (fetch cannot report upload progress).
        // The bubble shows a live bar as the bytes stream; once sent, it sits at
        // 100% while the server scans and stores.
        const { promise, cancel } = uploadFileWithProgress("/files", form, (fraction) =>
          patchUpload({ progress: fraction }),
        );

        // Register the optimistic bubble now, at 0%.
        setUploads((prev) => [
          ...prev,
          {
            clientId,
            fileName: prepared.name,
            sizeBytes: prepared.size,
            progress: 0,
            failed: false,
            cancel,
          },
        ]);

        promise
          .then(async (result) => {
            if (!result.ok) throw uploadFailure(result);

            const posted = await api
              .post<{ message: Message }>(`/channels/${channelId}/messages`, {
                // Just the filename; the attachment row below already shows the
                // icon, name and size. See the earlier note on why not markdown.
                body: prepared.name,
                attachmentId: (result.body as { file: { id: string } }).file.id,
              })
              .catch(() => null);

            // Refetched rather than patched locally: the server decides which
            // attachments are clean enough to be rendered.
            if (posted) await reloadLatest(channelId);

            // The real message now exists in the thread — drop the placeholder.
            removeUpload();
          })
          .catch((error: unknown) => {
            if (error instanceof DOMException && error.name === "AbortError") {
              removeUpload();
              return;
            }
            // The server sends a specific message and code on every rejection
            // (scanner down, too large, wrong type, malware); surface that
            // rather than a generic failure, since the reason changes what the
            // person should do — retry shortly vs. pick a smaller/different file.
            const message = uploadThrownMessage(error);
            // Leave the bubble in a failed state, carrying the reason, so the
            // user sees which file failed and why, and can dismiss it, rather
            // than it vanishing silently.
            patchUpload({ failed: true, error: message });
            toast(`${prepared.name}: ${message}`, { tone: "danger" });
          })
          .finally(() => {
            // `attaching` drives the composer's paperclip spinner; clear it only
            // when no uploads remain in flight.
            setUploads((prev) => {
              if (prev.every((u) => u.clientId === clientId || u.failed)) setAttaching(false);
              return prev;
            });
          });
      });
    },
    [channelId, reloadLatest, toast],
  );

  const dismiss = useCallback((upload: UploadingFile) => {
    upload.cancel();
    setUploads((prev) => prev.filter((x) => x.clientId !== upload.clientId));
  }, []);

  return { uploads, attaching, attach, dismiss };
}
